import { createContext, useContext, useState, useEffect } from 'react';

const AppContext = createContext();

const DEFAULT_SETTINGS = {
  maxFileSize: 10 * 1024 * 1024, // 10MB
  defaultZoom: 1.0,
  theme: 'light'
};

export const AppProvider = ({ children }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [currentView, setCurrentViewInternal] = useState('upload'); // upload, view, split, sign, form
  const [previousView, setPreviousView] = useState(null);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('gosplitpdf-settings');
    return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
  });
  const [notifications, setNotifications] = useState([]);
  const [fileSignatures, setFileSignatures] = useState({}); // Map of fileId -> signatures array

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('gosplitpdf-settings', JSON.stringify(settings));
  }, [settings]);

  // Wrapper for setCurrentView that tracks previous view for settings navigation
  const setCurrentView = (newView) => {
    if (newView === 'settings' && currentView !== 'settings') {
      setPreviousView(currentView);
    }
    setCurrentViewInternal(newView);
  };

  // Go back to the previous view (used when closing settings)
  const goBack = () => {
    if (previousView) {
      setCurrentViewInternal(previousView);
      setPreviousView(null);
    } else {
      setCurrentViewInternal('upload');
    }
  };

  // Add a file to the uploaded files list
  const addFile = (file, pdfDoc) => {
    const fileData = {
      id: Date.now() + Math.random(),
      file,
      pdfDoc,
      name: file.name,
      size: file.size,
      uploadedAt: new Date()
    };
    setUploadedFiles(prev => [...prev, fileData]);
    setCurrentFile(fileData);
    setCurrentView('view');
    return fileData;
  };

  // Remove a file from the list
  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    // Also remove signatures for this file
    setFileSignatures(prev => {
      const updated = { ...prev };
      delete updated[fileId];
      return updated;
    });
    if (currentFile?.id === fileId) {
      setCurrentFile(null);
      setCurrentView('upload');
    }
  };

  // Clear all files
  const clearAllFiles = () => {
    setUploadedFiles([]);
    setCurrentFile(null);
    setCurrentView('upload');
    setFileSignatures({});
  };

  // Get signatures for a file
  const getFileSignatures = (fileId) => {
    return fileSignatures[fileId] || [];
  };

  // Update signatures for a file
  const updateFileSignatures = (fileId, signatures) => {
    setFileSignatures(prev => ({
      ...prev,
      [fileId]: signatures
    }));
  };

  // Check if there's any state that would be lost
  const hasUnsavedWork = () => {
    if (uploadedFiles.length === 0) return false;
    // Check if any files have signatures
    const totalSignatures = Object.values(fileSignatures).reduce(
      (sum, sigs) => sum + (sigs?.length || 0),
      0
    );
    return uploadedFiles.length > 0 || totalSignatures > 0;
  };

  // Update settings
  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  // Reset settings to defaults
  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  // Show notification
  const showNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type // info, success, error, warning
    };
    setNotifications(prev => [...prev, notification]);

    // Auto-remove after 3 seconds
    setTimeout(() => {
      removeNotification(notification.id);
    }, 3000);
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const value = {
    uploadedFiles,
    currentFile,
    currentView,
    settings,
    notifications,
    fileSignatures,
    addFile,
    removeFile,
    clearAllFiles,
    setCurrentFile,
    setCurrentView,
    updateSettings,
    resetSettings,
    showNotification,
    removeNotification,
    getFileSignatures,
    updateFileSignatures,
    hasUnsavedWork,
    goBack
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
